import { Swiper } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import AOS from 'aos';
import { Collapse, Offcanvas, Modal } from 'bootstrap';
import { Fancybox } from "@fancyapps/ui";
import { initPageTransition } from './page-transition.js';

gsap.registerPlugin(ScrollTrigger);

document.addEventListener('DOMContentLoaded', () => {

    document.addEventListener('pageTransitionComplete', () => {
        AOS.refresh();
    }, { once: true });

    AOS.init({
        duration: 900,
        easing: 'ease-out-quart',
        once: true,
        offset: 120
    });

    initPageTransition();

    const header = document.querySelector('.main-header');
    if (header) {
        let lastScrollY = window.scrollY;
        window.addEventListener('scroll', () => {
            // Show/Hide Header
            if (window.scrollY > lastScrollY && window.scrollY > 100) {
                header.classList.add('navbar-hidden');
            } else {
                header.classList.remove('navbar-hidden');
            }

            // Background/Color Change
            if (window.scrollY > 50) {
                header.classList.add('scrolled');
            } else {
                header.classList.remove('scrolled');
            }

            lastScrollY = window.scrollY;
        });
    }

    initProductStack();
    initCounters();
    initFeaturesSwiper();
    initTestimonialsSwiper();
    initAccordion();
    initOffcanvasMenu();
    initHeroVideoLoop();
    initLegacyScroll();
    initInfiniteScroll();
    initExpertiseSwiper();
    initCollaborationSwiper();
    initFancybox();
    initLeadCapture();
});

// ─── Lead Capture Logic ──────────────────────────────────────────────────────
function initLeadCapture() {
    const downloadButtons = document.querySelectorAll('[data-bs-target="#downloadLeadModal"]');
    const leadForm = document.getElementById('leadCaptureForm');
    const requestedPdfInput = document.getElementById('requestedPdf');

    if (leadForm) {
        // When any download button is clicked, store the PDF name
        downloadButtons.forEach(btn => {
            btn.addEventListener('click', () => {
                const pdfName = btn.getAttribute('data-pdf') || 'Technical Documentation.pdf';
                if (requestedPdfInput) requestedPdfInput.value = pdfName;
            });
        });

        const nameInput = document.getElementById('leadName');
        const companyInput = document.getElementById('leadCompany');
        const emailInput = document.getElementById('leadEmail');
        const phoneInput = document.getElementById('leadPhone');

        // Clear validation styles on input
        [emailInput, phoneInput].forEach(input => {
            if (input) {
                input.addEventListener('input', () => {
                    input.classList.remove('is-invalid');
                });
            }
        });

        // Handle form submission
        leadForm.addEventListener('submit', (e) => {
            e.preventDefault();

            const name = nameInput ? nameInput.value.trim() : '';
            const company = companyInput ? companyInput.value.trim() : '';
            const email = emailInput ? emailInput.value.trim() : '';
            const phone = phoneInput ? phoneInput.value.trim() : '';

            let isValid = true;

            // Validate Mandatory Email
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!email || !emailRegex.test(email)) {
                if (emailInput) emailInput.classList.add('is-invalid');
                isValid = false;
            } else {
                if (emailInput) emailInput.classList.remove('is-invalid');
            }

            // Validate Mandatory Mobile Number
            const phoneRegex = /^[+]*[(]?[0-9]{1,4}[)]?[-\s./0-9]{6,15}$/;
            if (!phone || !phoneRegex.test(phone)) {
                if (phoneInput) phoneInput.classList.add('is-invalid');
                isValid = false;
            } else {
                if (phoneInput) phoneInput.classList.remove('is-invalid');
            }

            // Block access if mandatory fields are not valid
            if (!isValid) {
                return;
            }

            const pdfToDownload = requestedPdfInput && requestedPdfInput.value ? requestedPdfInput.value : 'EPD-Document.pdf';

            // Simulate lead data processing
            console.log('Lead Captured Successfully:', { name, company, email, phone, pdfToDownload });

            // Enable & trigger PDF access after successful submission of mandatory fields
            const downloadAnchor = document.createElement('a');
            downloadAnchor.href = 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf';
            downloadAnchor.target = '_blank';
            downloadAnchor.rel = 'noopener noreferrer';
            downloadAnchor.download = pdfToDownload;
            document.body.appendChild(downloadAnchor);
            downloadAnchor.click();
            document.body.removeChild(downloadAnchor);

            // Close the modal
            const modalEl = document.getElementById('downloadLeadModal');
            if (modalEl) {
                const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
                if (modal) modal.hide();
            }

            // Reset form
            leadForm.reset();
            if (emailInput) emailInput.classList.remove('is-invalid');
            if (phoneInput) phoneInput.classList.remove('is-invalid');
        });
    }
}


// ─── Testimonials Swiper ───────────────────────────────────────────────────
function initTestimonialsSwiper() {
    const swiper = new Swiper('.testimonials-swiper', {
        modules: [Navigation, Pagination],
        slidesPerView: 1,
        spaceBetween: 30,
        loop: true,
        speed: 800,
        pagination: {
            el: '.testimonial-pagination',
            clickable: true,
        },
        navigation: {
            nextEl: '.testimonial-next',
            prevEl: '.testimonial-prev',
        }
    });
}



// ─── Features Swiper ────────────────────────────────────────────────────────
function initFeaturesSwiper() {
    const swiper = new Swiper('.features-swiper', {
        modules: [Navigation, Pagination],
        slidesPerView: 1,
        spaceBetween: 20,
        loop: true,
        speed: 600,
        pagination: {
            el: '.features-pagination',
            clickable: true,
        },
        navigation: {
            nextEl: '.swiper-btn-next',
            prevEl: '.swiper-btn-prev',
        },
        breakpoints: {
            // when window width is >= 992px (Desktop)
            992: {
                slidesPerView: 3,
                spaceBetween: 30
            }
        }
    });
}
// ─── Expertise Swiper ────────────────────────────────────────────────────────
function initExpertiseSwiper() {
    const swiper = new Swiper('.expertise-swiper', {
        modules: [Navigation, Pagination],
        slidesPerView: 1,
        spaceBetween: 20,
        loop: true,
        speed: 600,
        pagination: {
            el: '.expertise-pagination',
            clickable: true,
        },
        navigation: {
            nextEl: '.expertise-next',
            prevEl: '.expertise-prev',
        },
        breakpoints: {
            // when window width is >= 992px (Desktop)
            992: {
                slidesPerView: 3,
                spaceBetween: 30
            }
        }
    });
}

// ─── Number Counter Animation ───────────────────────────────────────────────
function initCounters() {
    const counters = document.querySelectorAll('.stat-number');

    counters.forEach(counter => {
        const text = counter.innerText;
        // Extract number and suffix (like + or Million)
        const match = text.match(/([\d.]+)(.*)/);
        if (!match) return;

        const targetValue = parseFloat(match[1]);
        const suffix = match[2];
        const isFloat = match[1].includes('.');

        const obj = { value: 0 };

        gsap.to(obj, {
            value: targetValue,
            duration: 2,
            ease: 'power2.out',
            scrollTrigger: {
                trigger: counter,
                start: 'top 90%',
                toggleActions: 'play none none none'
            },
            onUpdate: () => {
                counter.innerText = (isFloat ? obj.value.toFixed(1) : Math.floor(obj.value)) + suffix;
            }
        });
    });
}


function initProductStack() {
    const cards = gsap.utils.toArray('.products-stack .product-spotlight');
    if (cards.length === 0) return;

    // Only apply sticky card stacking animation on desktop
    if (window.innerWidth >= 992) {
        cards.forEach((nextCard, i) => {
            if (i === 0) return;

            const prevCards = cards.slice(0, i);

            gsap.to(prevCards, {
                scale: (index) => 1 - (i - index) * 0.05,
                y: (index) => (i - index) * 50,
                ease: 'none',
                overwrite: 'auto',
                scrollTrigger: {
                    trigger: nextCard,
                    start: 'top bottom',
                    end: 'top 40px',
                    scrub: true,
                    markers: false,
                }
            });
        });
    }

    // Handle click on nav indicators
    const indicators = document.querySelectorAll('.product-nav-indicators span');
    indicators.forEach(span => {
        span.addEventListener('click', (e) => {
            const targetId = span.getAttribute('data-target');
            const targetEl = document.getElementById(targetId);

            if (targetEl) {
                const headerOffset = 100;
                const elementPosition = targetEl.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        });
    });
}

// ─── Accordion (Bootstrap Collapse) ──────────────────────────────────────────
function initAccordion() {
    const collapseEls = document.querySelectorAll('.accordion-collapse');
    collapseEls.forEach(el => new Collapse(el, { toggle: false }));
}

// ─── Mobile Offcanvas Menu ────────────────────────────────────────────────────
function initOffcanvasMenu() {
    const offcanvasEl = document.getElementById('mobileMenu');
    if (!offcanvasEl) return;

    const offcanvas = new Offcanvas(offcanvasEl);

    // Auto-close when a nav link inside the menu is clicked
    offcanvasEl.querySelectorAll('.mobile-nav-link').forEach(link => {
        link.addEventListener('click', () => offcanvas.hide());
    });
}





function initHeroVideoLoop() {
    const video = document.getElementById("heroVideo");
    if (video) {
        video.addEventListener('ended', () => {
            setTimeout(() => {
                video.currentTime = 0;
                video.play();
            }, 3000);
        });
    }
}

// ─── Our Legacy Section Animations ──────────────────────────────────────────
function initLegacyScroll() {
    const headline = document.querySelector('.legacy-headline');
    if (!headline) return;

    // Animate the main headline color
    gsap.to(headline, {
        color: '#1a1a1a',
        scrollTrigger: {
            trigger: headline,
            start: 'top 85%',
            end: 'top 40%',
            scrub: true,
            markers: false
        }
    });

    const fadeText = headline.querySelector('.fade-text');
    if (fadeText) {
        gsap.to(fadeText, {
            color: '#1a1a1a',
            scrollTrigger: {
                trigger: headline,
                start: 'top 75%',
                end: 'top 30%',
                scrub: true,
                markers: false
            }
        });
    }
}

function initInfiniteScroll() {
    const tracks = document.querySelectorAll('.infinite-carousel-track');
    if (tracks.length === 0) return;

    tracks.forEach(track => {
        const trackWidth = track.scrollWidth;
        const duration = track.classList.contains('logo-track') ? 35 : 55;

        // Clone internal elements to ensure seamless loop
        const clones = track.innerHTML;
        track.innerHTML += clones;

        gsap.to(track, {
            x: -trackWidth,
            duration: duration,
            ease: 'none',
            repeat: -1,
        });
    });
}

// ─── Collaboration Swiper ───────────────────────────────────────────────────
function initCollaborationSwiper() {
    const swiper = new Swiper('.collaboration-swiper', {
        modules: [Navigation, Pagination],
        slidesPerView: 1,
        spaceBetween: 20,
        loop: true,
        speed: 800,
        pagination: {
            el: '.collaboration-pagination',
            clickable: true,
        },
        navigation: {
            nextEl: '.collaboration-next',
            prevEl: '.collaboration-prev',
        },
        breakpoints: {
            992: {
                slidesPerView: 3,
                spaceBetween: 30
            }
        }
    });
}

function initFancybox() {
    Fancybox.bind("[data-fancybox]", {
        // Custom options
    });
}